import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { House, Receipt, Calculator as Calc, Car, CalendarBlank, ChartBar, MapPin } from '@phosphor-icons/react';
import { computeResults, formatEUR, formatNumber, NATIONAL_BENCHMARK } from '../lib/pendelLogic';

/**
 * Slug-specific focus panel shown ABOVE the calculator on SEO pages.
 * Each variant emphasises a different angle of the same underlying data.
 */
export default function FocusPanel({ variant, baseInput, city }) {
  if (variant === 'city') return <CityFocus city={city} baseInput={baseInput} />;
  if (variant === 'homeoffice') return <HomeofficeFocus baseInput={baseInput} />;
  if (variant === 'pauschale') return <PauschaleFocus baseInput={baseInput} />;
  if (variant === 'monatskosten') return <MonatsFocus baseInput={baseInput} />;
  if (variant === 'autofinanzierung') return <AutoFocus baseInput={baseInput} />;
  if (variant === 'jobwechsel') return <JobwechselFocus />;
  return null; // 'pendelkosten' uses standard layout
}

/* ---------- CITY: city-specific commute snapshot ---------- */
function CityFocus({ city, baseInput }) {
  const safeCity = city || { avg_distance: 0, avg_minutes: 0, fuel_type: 'diesel', consumption: 6 };
  const cityInput = {
    ...baseInput,
    distance_km: safeCity.avg_distance,
    minutes_one_way: safeCity.avg_minutes,
    fuel_type: safeCity.fuel_type,
    consumption: safeCity.consumption,
  };
  const r = useMemo(() => computeResults(cityInput), [JSON.stringify(cityInput)]);

  if (!city) return null;

  const distance_vs_de = Math.round(((city.avg_distance - NATIONAL_BENCHMARK.avg_distance_km) / NATIONAL_BENCHMARK.avg_distance_km) * 100);

  return (
    <FocusFrame
      icon={<MapPin size={18} weight="bold" />}
      eyebrow={`${city.name} · ${city.region}`}
      title={`Typische Pendler-Daten für ${city.name}`}
      testId="focus-city"
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-0 grid-border bg-white">
        <CityStat label="Ø Strecke" value={`${city.avg_distance.toFixed(1)} km`} sub={`${distance_vs_de >= 0 ? '+' : ''}${distance_vs_de}% vs. DE`} testId="city-distance" />
        <CityStat label="Ø Fahrzeit" value={`${city.avg_minutes} min`} sub="einfache Strecke" testId="city-minutes" />
        <CityStat label="Typische Jahreskosten" value={formatEUR(r.annual_cost)} sub={`${city.fuel_type === 'diesel' ? 'Diesel' : city.fuel_type.toUpperCase()} · ${city.consumption} L/100km`} testId="city-cost" />
        <CityStat label="Lebenszeit / Jahr" value={`${Math.round(r.annual_hours)} h`} sub={`${r.annual_days.toFixed(1)} Tage`} testId="city-hours" />
      </div>
      <p className="text-sm text-neutral-700 mt-6 max-w-3xl leading-relaxed">
        Pendler-Hotspots & Arbeitgeber in {city.name}: <span className="font-medium">{city.employer_hubs}</span>.
        Unten kannst du deine eigenen Werte eingeben — der lokale Spritpreis wird auf Wunsch live aus der Tankerkönig-API geladen.
      </p>
    </FocusFrame>
  );
}

function CityStat({ label, value, sub, testId }) {
  return (
    <div
      className="p-5 border-neutral-200 [&:not(:last-child)]:border-b md:[&:not(:last-child)]:border-b-0 md:[&:not(:last-child)]:border-r"
      data-testid={testId}
    >
      <div className="text-xs uppercase tracking-widest text-neutral-500">{label}</div>
      <div className="font-display text-2xl md:text-3xl mt-1 tnum">{value}</div>
      <div className="text-xs text-neutral-500 mt-1">{sub}</div>
    </div>
  );
}

function FocusFrame({ icon, eyebrow, title, children, testId }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="mt-12 grid-border bg-neutral-50"
      data-testid={testId}
    >
      <div className="p-6 md:p-10">
        <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-neutral-500">
          <span className="inline-flex w-8 h-8 bg-neutral-900 text-white items-center justify-center">{icon}</span>
          {eyebrow}
        </div>
        <h2 className="font-display text-3xl md:text-4xl tracking-tighter mt-3 max-w-2xl">{title}</h2>
        <div className="mt-6">{children}</div>
      </div>
    </motion.section>
  );
}

/* ---------- HOMEOFFICE: slider 0–5 days savings ---------- */
function HomeofficeFocus({ baseInput }) {
  const [hoDays, setHoDays] = useState(1);

  const baseline = useMemo(() => computeResults({ ...baseInput, office_days: baseInput.office_days || 5 }), [baseInput]);
  const reduced = useMemo(() => {
    const newOffice = Math.max(0, (baseInput.office_days || 5) - hoDays);
    return computeResults({ ...baseInput, office_days: newOffice });
  }, [baseInput, hoDays]);

  const saving_year = baseline.annual_cost - reduced.annual_cost;
  const saving_hours = baseline.annual_hours - reduced.annual_hours;

  return (
    <FocusFrame
      icon={<House size={18} weight="bold" />}
      eyebrow="Homeoffice-Rechner"
      title="Wie viele Tage Homeoffice nimmst du pro Woche?"
      testId="focus-homeoffice"
    >
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-center">
        <div className="md:col-span-2">
          <div className="flex items-baseline gap-2">
            <div className="font-display text-7xl tnum" data-testid="ho-days-value">{hoDays}</div>
            <div className="text-sm text-neutral-600">Homeoffice-Tag{hoDays === 1 ? '' : 'e'} / Woche</div>
          </div>
          <input
            type="range"
            min={0}
            max={5}
            step={1}
            value={hoDays}
            onChange={(e) => setHoDays(Number(e.target.value))}
            data-testid="ho-slider"
            className="w-full mt-4 accent-neutral-900"
          />
          <div className="flex justify-between text-xs text-neutral-500 font-mono mt-1">
            <span>0</span><span>1</span><span>2</span><span>3</span><span>4</span><span>5</span>
          </div>
        </div>
        <div className="md:col-span-3 grid grid-cols-2 gap-4">
          <Stat
            label="Ersparnis / Jahr"
            value={formatEUR(saving_year)}
            color="#2A9D8F"
            testId="ho-saving-eur"
          />
          <Stat
            label="Zeit zurück / Jahr"
            value={`${Math.round(saving_hours)} h`}
            color="#2A9D8F"
            testId="ho-saving-hours"
          />
          <Stat
            label="Neue Pendelkosten / Monat"
            value={formatEUR(reduced.monthly_cost)}
            testId="ho-new-monthly"
          />
          <Stat
            label="Pendelkosten / Monat bisher"
            value={formatEUR(baseline.monthly_cost)}
            mute
            testId="ho-old-monthly"
          />
        </div>
      </div>
      <p className="text-xs text-neutral-500 mt-6">
        Berechnung basiert auf deinen Eingaben unten (Strecke {Math.round(baseInput.distance_km || 0)} km, Verbrauch {Number(baseInput.consumption || 0).toFixed(1)} L). Verschiebe den Schieber und passe danach deine Daten an.
      </p>
    </FocusFrame>
  );
}

/* ---------- PENDLERPAUSCHALE: breakdown of the legal formula ---------- */
function PauschaleFocus({ baseInput }) {
  const distance = Number(baseInput.distance_km) || 0;
  const office_days = Number(baseInput.office_days) || 0;
  const days_year = office_days * 46; // ~46 working weeks
  const pp_low_km = Math.min(20, distance);
  const pp_high_km = Math.max(0, distance - 20);
  const pp_low_eur = pp_low_km * 0.30;
  const pp_high_eur = pp_high_km * 0.38;
  const pauschale_per_day = pp_low_eur + pp_high_eur;
  const pauschale_year = pauschale_per_day * days_year;

  const SAMPLE_TAX_RATE = 0.30;
  const tax_saving = pauschale_year * SAMPLE_TAX_RATE;

  return (
    <FocusFrame
      icon={<Receipt size={18} weight="bold" />}
      eyebrow="Pendlerpauschale 2025"
      title="So setzt sich deine Entfernungspauschale zusammen"
      testId="focus-pauschale"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-0 grid-border bg-white">
        <Row label="Strecke bis 20 km" right={`${pp_low_km.toFixed(0)} km × 0,30 € = ${formatEUR(pp_low_eur)}`} testId="pp-row1" />
        <Row label="Strecke ab km 21" right={`${pp_high_km.toFixed(0)} km × 0,38 € = ${formatEUR(pp_high_eur)}`} testId="pp-row2" />
        <Row label="Pauschale pro Arbeitstag" right={formatEUR(pauschale_per_day)} testId="pp-row3" />
        <Row label="Arbeitstage / Jahr" right={`${days_year.toFixed(0)} Tage`} testId="pp-row4" />
      </div>
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <Stat
          label="Pendlerpauschale / Jahr"
          value={formatEUR(pauschale_year)}
          color="#2A9D8F"
          testId="pp-year"
        />
        <Stat
          label="Steuerersparnis (bei 30% Grenzsatz)"
          value={`~ ${formatEUR(tax_saving)}`}
          testId="pp-tax"
        />
      </div>
      <p className="text-xs text-neutral-500 mt-4">
        Hinweis: Die Pauschale senkt nicht direkt deine Steuer, sondern dein zu versteuerndes Einkommen. Die tatsächliche Ersparnis hängt von deinem persönlichen Steuersatz ab (25–42% bei den meisten Arbeitnehmern).
      </p>
    </FocusFrame>
  );
}

/* ---------- MONATSKOSTEN: prominent monthly hero ---------- */
function MonatsFocus({ baseInput }) {
  const r = useMemo(() => computeResults(baseInput), [baseInput]);
  return (
    <FocusFrame
      icon={<CalendarBlank size={18} weight="bold" />}
      eyebrow="Pendeln pro Monat"
      title="Das kostet dein Arbeitsweg jeden Monat"
      testId="focus-monatskosten"
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <BigStat
          label="Kosten pro Monat"
          value={formatEUR(r.monthly_cost)}
          highlight
          testId="month-cost-eur"
        />
        <BigStat label="Stunden pro Monat" value={`${Math.round(r.annual_hours / 12)} h`} testId="month-hours" />
        <BigStat label="Kilometer pro Monat" value={`${formatNumber(r.monthly_km)} km`} testId="month-km" />
      </div>
      <p className="text-xs text-neutral-500 mt-4">
        Realistische Vollkosten inkl. Sprit, Verschleiß & anteiliger Versicherung — gerechnet mit 0,12 €/km Betriebskosten (ADAC-Mittelwert).
      </p>
    </FocusFrame>
  );
}

/* ---------- AUTO-FINANZIERUNG: real monthly cost incl. lease ---------- */
function AutoFocus({ baseInput }) {
  const [leaseRate, setLeaseRate] = useState(350);
  const r = useMemo(() => computeResults(baseInput), [baseInput]);
  const true_monthly = r.monthly_cost + Number(leaseRate || 0);
  const true_yearly = true_monthly * 12;

  return (
    <FocusFrame
      icon={<Car size={18} weight="bold" />}
      eyebrow="Auto finanzieren als Pendler"
      title="Was kostet das Pendel-Auto wirklich pro Monat?"
      testId="focus-auto"
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="grid-border bg-white p-5">
          <label className="text-xs uppercase tracking-widest text-neutral-500">Leasing / Finanzierungsrate</label>
          <div className="flex items-baseline gap-2 mt-2">
            <input
              type="number"
              data-testid="auto-lease-input"
              value={leaseRate}
              onChange={(e) => setLeaseRate(e.target.value)}
              className="w-full text-3xl font-display tracking-tight bg-transparent outline-none"
            />
            <span className="font-mono text-sm text-neutral-500">€ / Monat</span>
          </div>
        </div>
        <BigStat label="Betriebskosten / Monat" value={formatEUR(r.monthly_cost)} testId="auto-ops" />
        <BigStat label="ECHTE Kosten / Monat" value={formatEUR(true_monthly)} highlight testId="auto-real-monthly" />
      </div>
      <div className="mt-6 p-5 grid-border bg-white">
        <div className="text-xs uppercase tracking-widest text-neutral-500">Ehrliche Jahres-Sicht</div>
        <div className="font-display text-4xl md:text-5xl mt-1 tnum">{formatEUR(true_yearly)}</div>
        <p className="text-sm text-neutral-700 mt-2">
          Faustregel: Gesamtbelastung (Rate + Betrieb) sollte unter 15% deines Netto-Haushaltseinkommens liegen.
        </p>
      </div>
    </FocusFrame>
  );
}

/* ---------- JOBWECHSEL: pointer hint ---------- */
function JobwechselFocus() {
  return (
    <FocusFrame
      icon={<ChartBar size={18} weight="bold" />}
      eyebrow="Jobwechsel-Rechner"
      title="Lohnt sich dein neuer Job? Direkt unten vergleichen."
      testId="focus-jobwechsel"
    >
      <p className="text-neutral-700 max-w-2xl">
        Gib unten zuerst deine aktuelle Pendelsituation ein. Im Jobwechsel-Block kannst du dann dein neues Netto und die neue Strecke gegenrechnen — und siehst sofort, wie viel von der Gehaltserhöhung wirklich übrig bleibt.
      </p>
      <a
        href="#jobswitch"
        data-testid="jw-jump"
        className="mt-4 inline-flex items-center gap-2 bg-neutral-900 text-white h-12 px-5 text-xs uppercase tracking-widest hover:bg-black"
      >
        Direkt zum Jobwechsel-Vergleich
      </a>
    </FocusFrame>
  );
}

/* ---------- shared atoms ---------- */
function Stat({ label, value, color, mute, testId }) {
  return (
    <div data-testid={testId} className={`p-5 grid-border bg-white ${mute ? 'opacity-70' : ''}`}>
      <div className="text-xs uppercase tracking-widest text-neutral-500">{label}</div>
      <div className="font-display text-3xl mt-1 tnum" style={color ? { color } : undefined}>{value}</div>
    </div>
  );
}

function BigStat({ label, value, highlight, testId }) {
  return (
    <div
      data-testid={testId}
      className={`p-6 grid-border ${highlight ? 'bg-neutral-900 text-white border-neutral-900' : 'bg-white'}`}
    >
      <div className={`text-xs uppercase tracking-widest ${highlight ? 'text-white/70' : 'text-neutral-500'}`}>{label}</div>
      <div className="font-display text-4xl md:text-5xl mt-2 tnum">{value}</div>
    </div>
  );
}

function Row({ label, right, testId }) {
  return (
    <div
      className="p-4 md:p-5 border-neutral-200 [&:not(:last-child)]:border-b md:[&:nth-child(2n+1)]:border-r flex items-center justify-between gap-4"
      data-testid={testId}
    >
      <span className="text-sm text-neutral-700">{label}</span>
      <span className="font-mono text-sm">{right}</span>
    </div>
  );
}
